import { useCallback, useState } from "react";

import { Button, Dialog, DialogActions, DialogContent, Stack } from "@mui/material";
import PropTypes from "prop-types";

import { ChartOfAccountTypeEnum } from "@/features/chart-of-accounts";
import { OutletChartOfAccountSelect } from "@/features/outlet-chart-of-accounts";
import { apiClient, StatusEnum } from "@/features/shared";
import { CustomAutocomplete, CustomDialogTitle, useSnackbar } from "@/features/ui";

const CASH_AND_BANK_TYPES = [ChartOfAccountTypeEnum.CASH_AND_BANK];

// Dual-mode dialog: seeded from `initialValue` to edit an existing mapping,
// or empty to add a new one. Local state is committed to the parent field
// array only on save. Remount with a `key` so the initializers re-run.
export const PaymentMethodMappingFormDialog = ({
  open,
  onClose,
  onSave,
  outletId,
  excludeIds = [],
  initialValue = null,
}) => {
  const { showSnackbar } = useSnackbar();
  const isEdit = Boolean(initialValue);

  const [paymentMethod, setPaymentMethod] = useState(
    () => initialValue?.paymentMethod ?? null,
  );
  const [outletChartOfAccount, setOutletChartOfAccount] = useState(
    () => initialValue?.outletChartOfAccount ?? null,
  );

  const fetchPaymentMethods = useCallback(async () => {
    const {
      data: { data },
    } = await apiClient.get("payment-methods", {
      params: {
        order: "ASC",
        status: StatusEnum.ACTIVE,
      },
    });

    return data.filter((paymentMethod) => !excludeIds.includes(paymentMethod.id));
  }, [excludeIds]);

  const handleSave = () => {
    if (!paymentMethod) {
      showSnackbar("Metode pembayaran harus dipilih", "error");
      return;
    }

    if (!outletChartOfAccount) {
      showSnackbar("Kas & Bank harus dipilih", "error");
      return;
    }

    onSave({ paymentMethod, outletChartOfAccount });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <CustomDialogTitle
        title={isEdit ? "Ubah Metode Pembayaran" : "Tambah Metode Pembayaran"}
        onClose={onClose}
      />
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <CustomAutocomplete
            value={paymentMethod}
            onChange={setPaymentMethod}
            fetchOptions={fetchPaymentMethods}
            getOptionLabel={(option) => option?.name ?? ""}
            isOptionEqualToValue={(option, selectedOption) =>
              option.id === selectedOption.id
            }
            TextFieldProps={{
              label: "Metode Pembayaran",
              required: true,
              InputProps: {
                placeholder: "Pilih metode pembayaran",
              },
            }}
          />
          <OutletChartOfAccountSelect
            fullWidth
            value={outletChartOfAccount}
            onChange={setOutletChartOfAccount}
            outletId={outletId}
            types={CASH_AND_BANK_TYPES}
            TextFieldProps={{
              label: "Kas & Bank",
              required: true,
              InputProps: {
                placeholder: "Pilih Kas & Bank",
              },
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Batal</Button>
        <Button variant="contained" onClick={handleSave}>
          Simpan
        </Button>
      </DialogActions>
    </Dialog>
  );
};

PaymentMethodMappingFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  outletId: PropTypes.string.isRequired,
  excludeIds: PropTypes.arrayOf(PropTypes.string),
  initialValue: PropTypes.shape({
    paymentMethod: PropTypes.object,
    outletChartOfAccount: PropTypes.object,
  }),
};
